import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft } from 'lucide-react';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast.error('Bitte E-Mail-Adresse eingeben');
      return;
    }

    setIsLoading(true);

    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/forgot-password`, {
        email
      });
      
      setEmailSent(true);
      toast.success('E-Mail wurde versendet!');
    } catch (error) {
      console.error('Forgot password error:', error);
      // Even on error, show success (security)
      setEmailSent(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <style>{`
        .glassmorphic-card {
          background: rgba(255, 255, 255, 0.9);
          backdrop-filter: blur(20px);
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1);
        }
        
        .btn-primary {
          background: linear-gradient(135deg, #0040FF 0%, #0033CC 100%);
          transition: all 0.3s ease;
        }
        
        .btn-primary:hover {
          background-color: #0033CC;
          transform: translateY(-2px);
          box-shadow: 0 8px 16px rgba(0, 64, 255, 0.3);
        }
        
        .input-field {
          transition: all 0.3s ease;
        }
        
        .input-field:focus {
          border-color: #0040FF;
          box-shadow: 0 0 0 3px rgba(0, 64, 255, 0.1);
        }
      `}</style>
      
      <div className="min-h-screen flex items-center justify-center px-4 py-8" style={{ background: 'linear-gradient(180deg, #F9F7F4 0%, #F5F3F0 100%)' }}>
        <div className="absolute top-8 left-8">
          <button 
            onClick={() => navigate('/login')}
            className="flex items-center gap-2 px-5 py-3 rounded-full font-semibold border-2 border-gray-300 text-gray-700 hover:bg-white hover:shadow-lg transition-all duration-300"
          >
            <ArrowLeft className="w-4 h-4" />
            Zurück
          </button>
        </div>

        <div className="glassmorphic-card rounded-3xl p-8 md:p-10 w-full max-w-md border-2 border-white/40">
          {/* Logo/Brand */}
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold mb-2" style={{ color: '#5B3FFF' }}>AdSimplify</h1>
            <div className="w-16 h-1 bg-gradient-to-r from-indigo-600 to-indigo-400 mx-auto rounded-full"></div>
          </div>

          <h2 className="text-3xl font-bold mb-2" style={{ color: '#1A1A1A' }}>
            Passwort vergessen?
          </h2>
          <p className="text-gray-600 mb-8">
            {emailSent 
              ? 'E-Mail wurde versendet!'
              : 'Kein Problem! Wir schicken dir einen Reset-Link.'
            }
          </p>

          {emailSent ? (
            <div className="space-y-5">
              <div className="bg-green-50 border-2 border-green-200 rounded-2xl p-5">
                <p className="text-green-800 text-sm mb-2">
                  Falls ein Account mit dieser E-Mail existiert, wurde ein Reset-Link an <strong>{email}</strong> gesendet.
                </p>
                <p className="text-green-700 text-sm">
                  Der Link ist <strong>1 Stunde</strong> gültig.
                </p>
              </div>

              <div className="text-center space-y-3 pt-4">
                <p className="text-sm text-gray-600">
                  E-Mail nicht erhalten?
                </p>
                <button
                  onClick={() => setEmailSent(false)}
                  className="text-indigo-600 hover:text-indigo-700 text-sm font-semibold"
                >
                  Erneut senden
                </button>
              </div>

              <div className="pt-6 border-t-2 border-gray-200">
                <Link
                  to="/login"
                  className="w-full px-5 py-3 rounded-2xl font-semibold border-2 border-gray-300 text-gray-700 hover:border-indigo-400 hover:bg-white hover:text-indigo-600 transition-all duration-300 block text-center"
                >
                  ← Zurück zum Login
                </Link>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label htmlFor="email" className="block text-xs font-bold text-gray-500 uppercase mb-2">
                  E-Mail-Adresse *
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="deine@email.at"
                  className="input-field w-full px-5 py-4 border-2 border-gray-200 rounded-2xl focus:outline-none bg-white/90"
                  required
                  disabled={isLoading}
                />
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="btn-primary w-full py-4 px-5 rounded-2xl font-bold text-white shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Wird gesendet...' : 'Reset-Link senden'}
              </button>

              <div className="text-center pt-4">
                <Link
                  to="/login"
                  className="text-sm text-gray-600 hover:text-gray-900"
                >
                  Doch erinnert? <span className="text-indigo-600 font-semibold">Zum Login</span>
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </>
  );
};

export default ForgotPassword;
